import {Component, Input} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {CompanyInvoiceService} from "../../services/company-invoice.service";
import {CompanyField, CompanyInvoiceResponse} from "../../common/interfaces";
import {take} from "rxjs";
import {TranslatePipe} from "@ngx-translate/core";
import {NgForOf, NgIf} from "@angular/common";
import {oibValidator} from "../../utils/validators";
import {ErrorMessageService} from "../../services/error-message.util";

@Component({
  selector: 'app-company-invoice',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    NgIf,
    NgForOf
  ],
  templateUrl: './company-invoice.ts.component.html',
  styleUrl: './company-invoice.ts.component.sass',
  standalone: true,
})
export class CompanyInvoiceComponent {
  @Input() orderId!: number;
  errorMessage: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private readonly companyInvoice: CompanyInvoiceService,
    private readonly errorMessageService: ErrorMessageService,
  ) {}

  companyInvoiceForm!: FormGroup;
  fields: CompanyField[] = [];
  showCompanyInvoice: boolean = false;

  onClickCompanyInvoice(event: any): void {
    setTimeout(() => {
      if(!event.checked) {
        this.showCompanyInvoice = false;
      } else if(event.checked && !this.companyInvoiceForm) {
        this.companyInvoice.getCompanyInvoiceFormData()
          .pipe(take(1))
          .subscribe({
            next: (res: CompanyInvoiceResponse) => {
              const fields = res.data.fields;
              this.fields = fields.filter(f => f.visible);
              this.showCompanyInvoice = true;
              this.buildForm();
            }
          });
      } else if(this.companyInvoiceForm) {
        this.showCompanyInvoice = true;
      }
    }, 0)
  }

  private buildForm(): void {
    const group: any = {};
    this.fields.forEach(field => {
      group[field.name] = [
        field.value ?? '',
        field.mandatory ? Validators.required : null
      ];
    });

    this.companyInvoiceForm = this.formBuilder.group(group);

    const oibControl = this.companyInvoiceForm.get('rac_data_oib');
    const mbrControl = this.companyInvoiceForm.get('rac_data_mbr');

    if (!oibControl || !mbrControl) return;

    const existingValidators = oibControl.validator ? [oibControl.validator] : [];
    oibControl.setValidators([...existingValidators, oibValidator]);
    oibControl.updateValueAndValidity();

    setTimeout(() => {
      if(oibControl?.value && mbrControl?.value) {
        oibControl.enable({ emitEvent: false });
        mbrControl.enable({ emitEvent: false });
      } else if(oibControl?.value) {
        mbrControl.disable({ emitEvent: false });
      } else if(mbrControl?.value) {
        oibControl.disable({ emitEvent: false });
        oibControl.setValidators(existingValidators);
        oibControl.updateValueAndValidity();
      }
    },100)

    oibControl?.valueChanges.subscribe(value => {
      const val = String(value ?? '').trim();
      this.errorMessage = "";

      if (val) {
        if (mbrControl?.enabled) {
          mbrControl.setValue('', { emitEvent: false });
          mbrControl.disable({ emitEvent: false });
        }
      } else {
        if (!mbrControl?.value && mbrControl?.disabled) {
          mbrControl.enable({ emitEvent: false });
        }
      }

      this.updateDisabledState(oibControl!, mbrControl!);
    });

    mbrControl?.valueChanges.subscribe(value => {
      const val = String(value ?? '').trim();

      if (val) {
        if (oibControl?.enabled) {
          oibControl.setValue('', { emitEvent: false });
          oibControl.disable({ emitEvent: false });
        }
      } else {
        if (!oibControl?.value && oibControl?.disabled) {
          oibControl.enable({ emitEvent: false });
        }
      }

      this.updateDisabledState(oibControl!, mbrControl!);
    });
  }

  private updateDisabledState(oib: any, mbr: any) {
    const oibEl = document.getElementById('rac_data_oib');
    const mbrEl = document.getElementById('rac_data_mbr');

    if (oibEl) {
      oibEl.classList.toggle('disabled', oib.disabled);
    }
    if (mbrEl) {
      mbrEl.classList.toggle('disabled', mbr.disabled);
    }
  }

  onSubmitCompanyInvoice(): void {
    if (!this.companyInvoiceForm.valid) {
      this.companyInvoiceForm.markAllAsTouched();
      return;
    }

    const payload = this.companyInvoiceForm.value;
    this.companyInvoice.submitCompanyInvoiceData(payload, this.orderId)
      .pipe(take(1))
      .subscribe({
        next: res => {},
        error: err => {}
      });
  }

  public isFormValid(): boolean {
    if (!this.companyInvoiceForm) return false;

    this.companyInvoiceForm.markAllAsTouched();
    this.companyInvoiceForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    const oibControl = this.companyInvoiceForm.get('rac_data_oib');

    if (oibControl && oibControl.invalid) {
      this.errorMessage = this.errorMessageService.getErrorMessage("invalidOIB");
    }

    return this.companyInvoiceForm.valid;
  }

}
